// Home: what is waiting on Krish today, and the two run buttons he presses most.
// One call to home_summary. A block that fails returns its own error rather
// than blanking the page; a block for a section the account does not hold is
// simply absent.
import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { api } from "../services";
import type { HomeBlock, HomeReply, HomeSummary } from "../services/types";
import { useSession } from "../components/SessionContext";
import { PageHead, SectionHead } from "../components/dash/PageHead";
import { StatTiles } from "../components/dash/StatTiles";
import { DefinitionsPanel } from "../components/dash/DefinitionsPanel";
import { DataTable, type Column } from "../components/dash/DataTable";
import { ConfirmBar } from "../components/dash/ConfirmBar";
import { RunResults, useJobRunner } from "../components/dash/JobPanel";
import { formatISTDateTime } from "../lib/readAgo";

const FUP_TILES = ["fup.total", "fup.sendable", "fup.blocked", "fup.skipped.replied_or_booked"];
const COLD_TILES = ["cold.total", "cold.ready", "cold.sendable", "cold.blocked", "cold.capacity_total"];

function live<T>(b: HomeBlock<T>): b is T & { error?: undefined } {
  return !!b && !b.error;
}

const REPLY_COLUMNS: Column<HomeReply>[] = [
  { key: "show", label: "Show", cell: (r) => r.channel || "(unnamed)" },
  { key: "person", label: "Person", cell: (r) => r.person || "" },
  { key: "last", label: "Last inbound IST", cell: (r) => formatISTDateTime(r.last_inbound_at) },
  { key: "back", label: "Replied back", cell: (r) => (r.replied_back ? "yes" : "no") },
  {
    key: "open",
    label: "Open thread",
    cell: (r) => <Link to={`/replies?record=${encodeURIComponent(r.record)}`}>Open thread</Link>,
  },
];

export function Home() {
  const session = useSession();
  const runner = useJobRunner();
  const [where, setWhere] = useState<"fup" | "cold">("fup");

  const q = useQuery({
    queryKey: ["home:summary"],
    queryFn: async () => {
      const r = await api.home.summary();
      if (!r.ok) throw new Error(r.error);
      return r as unknown as HomeSummary;
    },
    staleTime: 60_000,
  });

  const d = q.data;
  const loading = q.isPending;
  const replies = d?.replies;
  const fups = d?.followups;
  const cold = d?.cold;
  const isAdmin = session?.role === "admin";
  const canDraftCold = isAdmin && live(cold) && cold.draft_enabled === true;
  const results = <RunResults runner={runner} onFinish={() => void q.refetch()} />;

  return (
    <>
      <PageHead title="Home" caption="Today, read from Airtable and the mailboxes." />

      {q.isError ? (
        <div className="note-neg"><p>{(q.error as Error).message}</p></div>
      ) : null}

      {loading || replies !== undefined ? (
        <section className="section compact">
          <SectionHead
            title="Replies waiting on you"
            right={<Link className="site" to="/replies">All replies</Link>}
          />
          {replies && replies.error ? (
            <div className="note-neg"><p>{replies.error}</p></div>
          ) : (
            <DataTable
              columns={REPLY_COLUMNS}
              rows={live(replies) ? replies.items : []}
              rowKey={(r) => r.record}
              loading={loading}
              loadingText="Reading Airtable."
              empty="No prospect is waiting on a reply."
            />
          )}
        </section>
      ) : null}

      {loading || fups !== undefined ? (
        <section className="section compact">
          <SectionHead
            title="Follow-ups today"
            right={<Link className="site" to="/followups">All follow-ups</Link>}
          />
          {loading ? (
            <p className="empty">Reading Airtable.</p>
          ) : fups && fups.error ? (
            <div className="note-neg"><p>{fups.error}</p></div>
          ) : live(fups) ? (
            <>
              <StatTiles
                tiles={[
                  { id: "fup.total", value: fups.total },
                  { id: "fup.sendable", value: fups.sendable },
                  { id: "fup.blocked", value: fups.blocked },
                  { id: "fup.skipped.replied_or_booked", value: fups.skipped?.replied_or_booked },
                ]}
                definitions={d?.definitions}
                readAt={d?.read_at}
              />
              <div className="run">
                <button
                  type="button"
                  className="btn"
                  disabled={!!runner.busy}
                  onClick={() => {
                    setWhere("fup");
                    void runner.run("Dry run", () => api.followups.dryRun("1,2,3,4,5"));
                  }}
                >
                  Dry run
                </button>
                <ConfirmBar
                  label="Create drafts"
                  confirmLabel="Yes, draft all steps now"
                  note="Writes real Gmail drafts and stamps Airtable. It does not send."
                  disabled={!!runner.busy}
                  busy={runner.busy === "Create drafts"}
                  onArm={() => setWhere("fup")}
                  onConfirm={() => runner.run("Create drafts", () => api.followups.draft("1,2,3,4,5", true))}
                />
              </div>
              {where === "fup" ? results : null}
            </>
          ) : null}
        </section>
      ) : null}

      {loading || cold !== undefined ? (
        <section className="section compact">
          <SectionHead
            title="Cold email today"
            right={<Link className="site" to="/cold">All cold email</Link>}
          />
          {loading ? (
            <p className="empty">Reading Airtable.</p>
          ) : cold && cold.error ? (
            <div className="note-neg"><p>{cold.error}</p></div>
          ) : live(cold) ? (
            <>
              <StatTiles
                tiles={[
                  { id: "cold.total", value: cold.total },
                  { id: "cold.ready", value: cold.ready },
                  { id: "cold.sendable", value: cold.sendable },
                  { id: "cold.blocked", value: cold.blocked },
                  { id: "cold.capacity_total", value: cold.capacity_total },
                ]}
                definitions={d?.definitions}
                readAt={d?.read_at}
              />
              <div className="run">
                <button
                  type="button"
                  className="btn"
                  disabled={!!runner.busy}
                  onClick={() => {
                    setWhere("cold");
                    void runner.run("Cold dry run", () => api.cold.dryRun(0, "", "evidence"));
                  }}
                >
                  Dry run
                </button>
                {canDraftCold ? (
                  <ConfirmBar
                    label="Draft cold"
                    confirmLabel="Yes, draft cold now"
                    note="Drafts only, capped per mailbox. Nothing sends."
                    disabled={!!runner.busy}
                    busy={runner.busy === "Draft cold"}
                    onArm={() => setWhere("cold")}
                    onConfirm={() => runner.run("Draft cold", () => api.cold.draft(true))}
                  />
                ) : null}
              </div>
              {where === "cold" ? results : null}
            </>
          ) : null}
        </section>
      ) : null}

      {d ? (
        <DefinitionsPanel ids={[...FUP_TILES, ...COLD_TILES]} definitions={d.definitions} readAt={d.read_at} />
      ) : null}
    </>
  );
}
